import { FC, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import { checklistReservaService, mensagemErroChecklistReserva } from '@/services/checklistReservaService'
import { CODIGOS_MOTIVO_RECUSA, ROTULOS_CODIGO_MOTIVO } from '@/constants/statusChecklist'
import type { CodigoMotivoRecusa, DecisaoItemChecklistResponse } from '@/types/checklist'
import type { ItemChecklistReserva } from '@/types/estruturacao'
import { rotuloDoItem, statusDoItem } from '@/utils/checklistReserva'

const MAX_MOTIVO = 500

interface Props {
  open: boolean
  reservaId: string
  item: ItemChecklistReserva | null
  onFechar: () => void
  onRecusado: (decisao: DecisaoItemChecklistResponse) => void
}

/**
 * Recusar (ou invalidar, se já VALIDADO) um item do checklist — back-office. Motivo obrigatório
 * (até 500) e código opcional; o consultor vê o motivo e o código, nunca quem recusou. Não muda
 * o status da reserva: o item vira "Documento recusado" na Central de Pendências do consultor.
 */
export const RecusarItemDialog: FC<Props> = ({ open, reservaId, item, onFechar, onRecusado }) => {
  const [motivo, setMotivo] = useState('')
  const [codigo, setCodigo] = useState<CodigoMotivoRecusa | ''>('')
  const [salvando, setSalvando] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setMotivo('')
      setCodigo('')
      setErro(null)
    }
  }, [open, item])

  if (!item) return null
  const invalidar = statusDoItem(item) === 'VALIDADO'
  const rotulo = rotuloDoItem(item)
  const podeSalvar = !salvando && motivo.trim().length > 0 && motivo.length <= MAX_MOTIVO

  const salvar = async () => {
    if (!item.chave || !podeSalvar) return
    setSalvando(true)
    setErro(null)
    try {
      onRecusado(
        await checklistReservaService.recusar(reservaId, item.chave, {
          motivo: motivo.trim(),
          codigoMotivo: codigo || null,
        })
      )
    } catch (e) {
      setErro(mensagemErroChecklistReserva(e, 'Não foi possível recusar o documento.'))
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Dialog open={open} onClose={salvando ? undefined : onFechar} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        {invalidar ? 'Invalidar' : 'Recusar'}: {rotulo}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          O consultor verá o motivo na Central de Pendências e poderá trocar o arquivo quando a
          reserva estiver com pendência aberta.
        </Typography>
        <TextField
          select
          label="Código do motivo (opcional)"
          value={codigo}
          onChange={(e) => setCodigo(e.target.value as CodigoMotivoRecusa | '')}
          fullWidth
          disabled={salvando}
        >
          <MenuItem value="">Sem código</MenuItem>
          {CODIGOS_MOTIVO_RECUSA.map((c) => (
            <MenuItem key={c} value={c}>
              {ROTULOS_CODIGO_MOTIVO[c]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          label="Motivo"
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          required
          fullWidth
          multiline
          minRows={3}
          sx={{ mt: 2 }}
          disabled={salvando}
          error={motivo.length > MAX_MOTIVO}
          helperText={`Obrigatório — ${motivo.length}/${MAX_MOTIVO}`}
        />
        {erro && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {erro}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar} disabled={salvando}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={() => void salvar()}
          disabled={!podeSalvar}
          startIcon={salvando ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {invalidar ? 'Invalidar' : 'Recusar'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RecusarItemDialog
